import { useEffect, useRef } from "react";
import { ValidatedForm } from "remix-validated-form";
import { campaignPhotoUploadValidator, useLocalePath } from "@commercekit/remix";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import WvFormHiddenInputs from "~/components/atoms/WvFormHiddenInputs";
import { GoogleReCaptchaCheckbox } from "@google-recaptcha/react";
import PhotoDropzone from "./PhotoDropzone";
import CampaignField from "./CampaignField";
import CampaignCardHeader from "./CampaignCardHeader";
import CampaignTerminalState from "./CampaignTerminalState";
import CampaignConsent from "./CampaignConsent";
import { useCampaignForm } from "./useCampaignForm";

const ButtonSpinner = () => (
  <svg
    className="btn-spinner"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle
      cx="12" cy="12" r="9"
      stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeDasharray="40 20"
    />
  </svg>
);

const WvCampaignPhotoUploadForm = () => {
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();

  const {
    formRef,
    files, setFiles,
    fileError, setFileError,
    consent, setConsent,
    consentError, setConsentError,
    setNameValue, setEmailValue,
    captchaToken, setCaptchaToken,
    isBusy, actionData, isFormReady,
    RESOURCE_ACTION, handleSubmit,
  } = useCampaignForm();

  const homeHref = localePath("/");
  const headerProps = {
    title: $pt("campaign_photo_upload_title"),
    subtitle: $pt("campaign_photo_upload_subtitle"),
  };

  const cardRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  // When a submission resolves, bring the result into view. The form is tall and
  // the user is scrolled near the bottom (submit button); on a terminal state the
  // form is replaced by a much shorter card (leaving the viewport scrolled past it),
  // and on error the banner can be off-screen. Scroll to the relevant element.
  useEffect(() => {
    if (
      actionData?.success ||
      actionData?.limitReached ||
      actionData?.imageQuotaReached
    ) {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (!isBusy && actionData?.error) {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [actionData, isBusy]);

  if (actionData?.success) {
    return (
      <div className="campaign-card" ref={cardRef}>
        <CampaignCardHeader {...headerProps} />
        <CampaignTerminalState
          variant="success"
          message={$pt("campaign_photo_upload_success")}
          homeHref={homeHref}
        />
      </div>
    );
  }

  if (actionData?.limitReached) {
    return (
      <div className="campaign-card" ref={cardRef}>
        <CampaignCardHeader {...headerProps} />
        <CampaignTerminalState
          variant="limit"
          message={$pt("campaign_photo_upload_limit_reached")}
          homeHref={homeHref}
        />
      </div>
    );
  }

  if (actionData?.imageQuotaReached) {
    return (
      <div className="campaign-card" ref={cardRef}>
        <CampaignCardHeader {...headerProps} />
        <CampaignTerminalState
          variant="limit"
          message={$pt("campaign_photo_upload_quota_reached")}
          homeHref={homeHref}
        />
      </div>
    );
  }

  return (
    <div className="campaign-card" ref={cardRef}>
      <CampaignCardHeader {...headerProps} />
      <div className="campaign-card__body">
        <ValidatedForm
          validator={campaignPhotoUploadValidator}
          method="post"
          action={RESOURCE_ACTION}
          encType="multipart/form-data"
          formRef={formRef}
          onSubmit={handleSubmit}
        >
          <WvFormHiddenInputs data={{ _action: "SUBMIT_PHOTOS" }} />
          <input type="hidden" name="recaptchaValue" value={captchaToken ?? ""} />

          <CampaignField
            name="name"
            label={$pt("campaign_name_label")}
            placeholder={$pt("campaign_name_placeholder") ?? undefined}
            required
            onValueChange={setNameValue}
          />

          <CampaignField
            name="email"
            label={$pt("campaign_email_label")}
            type="email"
            placeholder={$pt("campaign_email_placeholder") ?? undefined}
            required
            onValueChange={setEmailValue}
          />

          <PhotoDropzone
            files={files}
            onChange={(selected: File[]) => {
              setFiles(selected);
              if (selected.length > 0) setFileError(null);
            }}
            error={fileError ?? undefined}
            isBusy={isBusy}
          />

          <p className="campaign-required-note">
            {$pt("campaign_required_fields_note") ?? "Velden met een * zijn verplicht."}
          </p>

          <CampaignConsent
            checked={consent}
            onChange={(checked) => {
              setConsent(checked);
              if (checked) setConsentError(null);
            }}
            error={consentError}
          />

          <div className="campaign-captcha">
            {/* Pass the stable useState setter directly. An inline arrow would
                change identity each render, and the widget's render effect lists
                onChange as a dependency — a new identity destroys & recreates the
                widget, wiping the solved (green check) state. */}
            <GoogleReCaptchaCheckbox onChange={setCaptchaToken} />
          </div>

          {!isBusy && actionData?.error && (
            <div className="campaign-error-banner" role="alert" ref={errorRef}>
              {$pt(actionData.error) ?? actionData.error}
            </div>
          )}

          <button
            type="submit"
            className="campaign-submit-btn"
            disabled={isBusy || !isFormReady}
          >
            {isBusy && <ButtonSpinner />}
            {isBusy ? (
              $pt("campaign_submitting_label")
            ) : (
              <>
                {$pt("campaign_submit_button")}
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </>
            )}
          </button>
        </ValidatedForm>
      </div>
    </div>
  );
};

export default WvCampaignPhotoUploadForm;
